'use strict';

var FirebaseObject = require('./FirebaseObject');
var KayleeBase = require('./KayleeBase');
var KayleeCache = require('./KayleeCache');
var KayleeUtil = require('./KayleeUtil');
var sutl = require('../sutl/Sutl');

var KayleeDirectory = {
    fromJson: fromJson,
    getDirectory: getDirectory,
};

function addUpdatesToRemoveDirectory(update, directoryId, companyId) {
    // Note: this is only intended to be used with company directories, not
    // room directories. Room directories get removed in the room removal code.

    update['directories/' + directoryId] = null;
    update['index/directoriesByCompany/' + companyId + '/' + directoryId] = null;

    return KayleeUtil.loadSitesByCompanyIndex({ companyId: companyId }, false)
    .then(function(obj) {
        var promises = [];

        if (obj.sitesByCompany === null) obj.sitesByCompany = {};

        for (var siteId in obj.sitesByCompany) {
            if (!obj.sitesByCompany.hasOwnProperty(siteId)) continue;
            promises.push(KayleeUtil.loadRoomsBySiteIndex({ siteId: siteId }, false));
        }

        return Promise.all(promises);
    })
    .then(function(results) {
        var roomPromises = [];

        results.forEach(function(result) {
            if (result.roomsBySite === null) result.roomsBySite = {};

            for (var roomId in result.roomsBySite) {
                if (!result.roomsBySite.hasOwnProperty(roomId)) continue;
                roomPromises.push(KayleeUtil.loadRoomData({ roomId: roomId }));
            }
        });

        return Promise.all(roomPromises);
    })
    .then(function(objs) {
        objs.forEach(function(obj) {
            var companyDirectories =
                sutl.getNode(obj, [ 'room', 'settings', 'companyDirectories' ]);

            if (companyDirectories === null) return;

            for (var id in companyDirectories) {
                if (!companyDirectories.hasOwnProperty(id)) continue;

                if (id === directoryId) {
                    update['rooms/' + obj.roomId +
                        '/settings/companyDirectories/' + id] = null;
                }
            }
        });

        return update;
    });
}

function fromJson(data) {
    var reqProps = [ '$id' ];
    return FirebaseObject.$fromJson(data, 'Directory', reqProps, reqProps,
        function(data) { return getDirectory(data.$id); }
    );
}

function getDirectory(directoryId) {
    var key = 'Directory: ' + directoryId;
    var directory = KayleeCache.get(key);

    if (directory === null) {
        directory = new Directory(directoryId);
        KayleeCache.put(key, directory);
    }

    return directory;
}

function Directory(directoryId) {
    this.$nodeType = 'directory';
    this.$entryAddedListeners  = [];
    this.$entryRemovedListeners = [];
    this.$entryChangedListeners = [];

    var path = 'directories/' + directoryId;

    FirebaseObject.call(this, KayleeBase.app.database().ref(path));

    this.$ref.child('entries').on('child_added', this.$entryAddedCb, this);
    this.$ref.child('entries').on('child_changed', this.$entryChangedCb, this);
    this.$ref.child('entries').on('child_removed', this.$entryRemovedCb, this);
}

Directory.prototype = Object.create(FirebaseObject.prototype);
Directory.prototype.constructor = Directory;

Directory.prototype.$toJson = function() {
    return FirebaseObject.prototype.$toJson.call(this, [ '$id' ]);
};

Directory.prototype.$canInviteContact = function(contact) {
    return contact.type === 'zoom';
};

Directory.prototype.$canInviteEntry = function(entry) {
    if (!entry.hasOwnProperty('contacts')) return false;

    for (var i = 0; i < entry.contacts; i++) {
        if (this.canInviteContact(entry.contacts[i])) return true;
    }

    return false;
};

Directory.prototype.$destroy = function() {
    this.$ref.child('entries').off('child_added', this.$entryAddedCb, this);
    this.$ref.child('entries').off('child_changed', this.$entryChangedCb, this);
    this.$ref.child('entries').off('child_removed', this.$entryRemovedCb, this);

    this.$entryAddedListeners.length = 0;
    this.$entryChangedListeners.length = 0;
    this.$entryRemovedListeners.length = 0;

    FirebaseObject.prototype.$destroy.call(this);
};

Directory.prototype.$entryAddedCb = function(snap) {
    this.$entryAddedListeners.forEach(function(listener) {
        listener(snap.key, snap.val());
    });
};

Directory.prototype.$entryChangedCb = function(snap) {
    this.$entryChangedListeners.forEach(function(listener) {
        listener(snap.key, snap.val());
    });
};

Directory.prototype.$entryRemovedCb = function(snap) {
    this.$entryRemovedListeners.forEach(function(listener) {
        listener(snap.key);
    });
};

Directory.prototype.$offEntryAdded = function(listener) {
    for (var i = 0; i < this.$entryAddedListeners.length; i++) {
        if (this.$entryAddedListeners[i] === listener) {
            this.$entryAddedListeners.splice(i, 1);
            break;
        }
    }
};

Directory.prototype.$offEntryChanged = function(listener) {
    for (var i = 0; i < this.$entryChangedListeners.length; i++) {
        if (this.$entryChangedListeners[i] === listener) {
            this.$entryChangedListeners.splice(i, 1);
            break;
        }
    }
};

Directory.prototype.$offEntryRemoved = function(listener) {
    for (var i = 0; i < this.$entryRemovedListeners.length; i++) {
        if (this.$entryRemovedListeners[i] === listener) {
            this.$entryRemovedListeners.splice(i, 1);
            break;
        }
    }
};

Directory.prototype.$onEntryAdded = function(listener, initialCall) {
    if (typeof initialCall === 'undefined') initialCall = true;

    this.$entryAddedListeners.push(listener);

    if (!initialCall || typeof this.entries === 'undefined') return;

    var keys = Object.keys(this.entries);

    for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        listener(key, this.entries[key]);
    }
};

Directory.prototype.$onEntryChanged = function(listener) {
    this.$entryChangedListeners.push(listener);
};

Directory.prototype.$onEntryRemoved = function(listener) {
    this.$entryRemovedListeners.push(listener);
};

Directory.prototype.$remove = function() {
    return addUpdatesToRemoveDirectory({}, this.$id, this.companyId)
    .then(KayleeBase.update);
};

Directory.prototype.$save = function() {
    this.modified = Date.now();
    if (!this.hasOwnProperty('created')) this.created = this.modified;
    return FirebaseObject.prototype.$save.call(this);
};

// Bypass FirebaseObject.$save for improved performance. Part of SNTY-1769

Directory.prototype.$setEntries = function(entries, updateTimestamps) {
    var self = this;

    if (updateTimestamps === undefined) updateTimestamps = true;

    if (updateTimestamps) {
        var modified = Date.now();

        return self.$ref.child('beforeModified').set(modified)
        .then(function() {
            return self.$ref.child('entries').set(entries);
        })
        .then(function() {
            modified = Date.now();
            if (!self.hasOwnProperty('created')) return self.$ref.child('created').set(modified);
        })
        .then(function() {
            return self.$ref.child('modified').set(modified);
        });
    } else {
        return self.$ref.child('entries').set(entries);
    }
};

Directory.prototype.$setEntry = function(key, value) {
    return this.$ref.child('entries/' + key).set(value);
};

Directory.prototype.toString = function() {
    return '[Directory (' + this.$id + ')]';
};

module.exports = KayleeDirectory;
